import { useClassFiltersStore } from '@stores/classes';
import { useQuery } from '@tanstack/react-query';
import { useUserContext } from 'providers/useUserContext';
import { PaymentMethod } from 'screens/Checkout/CheckoutForm';
import { MindbodyClass, MindbodyWaitlistEntry, PickaspotClass } from 'types/class';
import { ClientCompleteInfo, ClientPurchases, ClientService, ClientVisit, MindbodyUser } from 'types/client';
import { ClientContract } from 'types/clientContract';
import { MindbodyContract } from 'types/contract';
import { GiftCard } from 'types/giftCard';
import { Service } from 'types/service';
import { MindbodySite } from 'types/site';
import { Staff } from 'types/staff';
import { MindbodyLocation } from 'types/studio';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP } from 'utils/http';
import { useCoachStudioParams } from './useCoachStudioParams';

export type UseListClassesParams = {
  week: number;
  year: number;
  studioId?: number | null;
  coachId?: number | null;
  coachIds?: number[];
};

export function useListClasses(params: UseListClassesParams) {
  const { isStudioUrl, id } = useCoachStudioParams();
  if (isStudioUrl) params.studioId = id;

  const coachIds = useClassFiltersStore((state) => state.coachIds);
  const shortCodeCoachId = mondayMindbody?.coach?.length && parseInt(mondayMindbody.coach, 10);

  // We delete them to avoid having both properties in the query
  delete params.coachId;
  delete params.coachIds;

  // Handle coach filters
  if (shortCodeCoachId) {
    params.coachId = shortCodeCoachId;
  } else if (coachIds.length === 1 && params.studioId !== null) {
    // IMPORTANT: On n'envoie coachId à l'API QUE si UN SEUL coach ET un studio unique sont sélectionnés
    // L'API Mindbody utilise une logique ET (pas OU) donc plusieurs coachIds ne fonctionnera pas
    // Si plusieurs coaches ou studioId est null, le filtrage se fera en frontend
    params.coachId = coachIds[0];
  }

  if (shortCodeCoachId) {
    // Si un coach est spécifié dans l'URL, on le gère côté client
    params.studioId = null;
  }

  return useQuery({
    queryKey: ['/class', JSON.stringify(params)],
    queryFn: () => HTTP.get<MindbodyClass[]>('/class', params).send(),
    staleTime: 0,
  });
}

export function useListCoaches() {
  return useQuery({ queryKey: ['coaches'], queryFn: () => HTTP.get<Staff[]>('/staff/coaches').send() });
}

export function useListPurchases() {
  const { user, isLoggedIn } = useUserContext();
  return useQuery({
    queryKey: ['/client/purchases', user?.Id],
    queryFn: () => HTTP.get<ClientPurchases[]>(`/client/purchases`).send(),
    retry: false,
    enabled: isLoggedIn,
  });
}
export function useListVisits() {
  const { user, isLoggedIn } = useUserContext();
  return useQuery({
    queryKey: ['/client/visits', user?.Id],
    queryFn: () => HTTP.get<ClientVisit[]>(`/client/visits`).send(),
    retry: false,
    enabled: isLoggedIn,
  });
}
/**
 * Historique Zingfit (pré-migration) du client connecté, par site Mindbody de
 * sa région : { "5736526": 416, "5736551": 97, ... }. Paris uniquement ; un
 * site absent de la réponse = pas de donnée fiable (le compteur retombe alors
 * sur le seul calcul Mindbody pour ce site).
 */
export function useZingfitVisits() {
  const { user, isLoggedIn } = useUserContext();
  return useQuery({
    queryKey: ['/client/zingfit-visits', user?.Id],
    queryFn: () => HTTP.get<Record<string, number>>(`/client/zingfit-visits`).send(),
    retry: false,
    enabled: isLoggedIn,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60,
  });
}

export function useClientWaitlist() {
  const { user, isLoggedIn } = useUserContext();
  return useQuery({
    queryKey: ['/client/waitlist', user?.Id],
    queryFn: () => HTTP.get<ClientVisit[]>(`/client/waitlist`).send(),
    retry: false,
    enabled: isLoggedIn,
  });
}

export function useClientRefereralTypes() {
  return useQuery({
    queryKey: ['referalTypes'],
    queryFn: () => HTTP.get<string[]>('/client/referralTypes').send(),
    refetchOnMount: true,
  });
}

export function useClientRequiredFields() {
  return useQuery({
    queryKey: ['/client/requiredFields'],
    queryFn: () =>
      HTTP.get<{ required_fields: string[]; custom_fields: { Id: string; Name: string; DataType: string }[] }>(
        '/client/requiredFields',
      ).send(),
    retry: false,
    gcTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
  });
}

export function useListClientServices() {
  const { user, isLoggedIn } = useUserContext();
  return useQuery({
    queryKey: ['/client/services', user?.Id],
    queryFn: () => HTTP.get<ClientService[]>(`/client/services`).send(),
    retry: false,
    enabled: isLoggedIn,
  });
}

/**
 * Contrats (abonnements) actifs/inactifs du client connecté. Récupéré depuis
 * `GET /public/v6/client/clientcontracts` côté Mindbody via la route WP REST
 * dédiée. Distinct de `useListContracts` qui renvoie le catalogue.
 */
export function useListClientContracts() {
  const { user, isLoggedIn } = useUserContext();
  return useQuery({
    queryKey: ['/client/clientcontracts', user?.Id],
    queryFn: () => HTTP.get<ClientContract[]>(`/client/clientcontracts`).send(),
    retry: false,
    enabled: isLoggedIn,
    staleTime: 60 * 1000,
  });
}
export function useListServices(classId?: string | number) {
  return useQuery({
    queryKey: ['/sale/services', classId],
    queryFn: () => HTTP.get<Service[]>(`/sale/services`, { classId }).send(),
    retry: false,
  });
}

/**
 * Live Mindbody contracts (used by the new admin-driven Tarifs page to
 * render the "Abonnements" section). The legacy `/sale/contracts` route is
 * hardcoded to locationId=1 by default but now accepts an optional override
 * via the second arg — the new ServicesContracts component reads
 * `pricingConfig.defaultLocationId` and passes it through.
 */
export function useListContracts(locationId?: number) {
  return useQuery({
    queryKey: ['/sale/contracts', locationId ?? 1],
    queryFn: () =>
      HTTP.get<MindbodyContract[]>(
        '/sale/contracts',
        locationId ? { locationId } : {},
      ).send(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useListGiftCards(classId?: string | number) {
  return useQuery({
    queryKey: ['/sale/giftcards', classId],
    queryFn: () => HTTP.get<GiftCard[]>(`/sale/giftcard`, { classId }).send(),
    retry: false,
  });
}

export function useListLocations() {
  return useQuery({ queryKey: ['locations'], queryFn: () => HTTP.get<MindbodyLocation[]>('/site/locations').send() });
}

export function useCrossRegionalAssociations() {
  return useQuery({
    queryKey: ['/client/crossRegionalAssociations'],
    queryFn: () => HTTP.get<CrossRegionalLocationsAPIResponse>('/client/crossRegionalAssociations').send(),
  });
}

export function useListSites() {
  return useQuery({ queryKey: ['sites'], queryFn: () => HTTP.get<MindbodySite[]>('/site/sites').send() });
}

export function useFetchClientCompleteInfo() {
  const { user, isLoggedIn } = useUserContext();

  return useQuery({
    queryKey: ['/client/completeinfo', user?.Id],
    queryFn: () => HTTP.get<ClientCompleteInfo>('/client/completeinfo').send(),
    retry: false,
    enabled: isLoggedIn,
    staleTime: 1000,
  });
}

export function useFetchMe() {
  const { user, isLoggedIn } = useUserContext();

  return useQuery({
    queryKey: ['/client/me', user?.Id],
    queryFn: () => HTTP.get<MindbodyUser>('/client/me').send(),
    retry: false,
    enabled: isLoggedIn,
    staleTime: 1000,
  });
}

export function useFetchClass(id?: string, isEnrollmentInURL?: boolean) {
  const { isLoggedIn } = useUserContext();

  return useQuery<MindbodyClass, ApiClientException>({
    queryKey: ['/class', id, isEnrollmentInURL],
    queryFn: () => {
      const url = isEnrollmentInURL ? `/class/${id}/enrollment` : `/class/${id}`;
      return HTTP.get<MindbodyClass>(url).send();
    },
    retry: false,
    staleTime: 0,
    enabled: isLoggedIn && !!id,
  });
}

export function useFetchEnrollment(id?: string) {
  const { isLoggedIn } = useUserContext();

  return useQuery<MindbodyClass, ApiClientException>({
    queryKey: ['/class/enrollment', id],
    queryFn: () => HTTP.get<MindbodyClass>(`/class/enrollment/${id}`).send(),
    retry: false,
    staleTime: 0,
    enabled: isLoggedIn && !!id,
  });
}

export function useFetchClassWaitlistEntry(id?: string) {
  const { isLoggedIn } = useUserContext();

  return useQuery<MindbodyWaitlistEntry, ApiClientException>({
    queryKey: ['/class', id, '/waitlist'],
    queryFn: () => HTTP.get<MindbodyWaitlistEntry>(`/class/${id}/waitlist`).send(),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    enabled: isLoggedIn && !!id,
  });
}

export function useFetchPickASpot(classId: string | number) {
  const { isLoggedIn } = useUserContext();
  return useQuery({
    queryFn: () => HTTP.get<PickaspotClass>(`/class/${classId}/pickaspot`).send(),
    queryKey: ['/class', classId, '/pickaspot'],
    staleTime: 0,
    gcTime: 0,
    retry: false,
    enabled: isLoggedIn,
  });
}

export function useFetchCustomPaymentMethods() {
  const { isLoggedIn } = useUserContext();
  return useQuery({
    queryFn: () => HTTP.get<PaymentMethod[]>(`/sale/custompaymentmethods`).send(),
    queryKey: ['/sale/custompaymentmethods'],
    staleTime: 0,
    gcTime: 0,
    retry: false,
    enabled: isLoggedIn,
  });
}

export function useFetchAvailablePaymentPlans(amount: number) {
  const { isLoggedIn } = useUserContext();
  return useQuery({
    queryFn: () => HTTP.get<PaymentMethod[]>(`/sale/almapaymentplans`, { amount }).send(),
    queryKey: ['/sale/almapaymentplans', amount],
    staleTime: 0,
    gcTime: 0,
    retry: false,
    enabled: isLoggedIn,
  });
}
